// ==============================
// Family tree dialog (User Management → Group tab)
// ==============================
// Accounts don't carry a structured parent→child link — `parent` is a free-text
// name — so this is a simple two-row demo built from the accounts that share a
// group (phone / email / …): parents (accounts with no parent) sit on the top
// row joined by a horizontal line, children sit on the second row. A vertical
// line links the two rows.

import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { apiGet } from '../utils/apiClient';
import { User } from '../types/UserTable.type';
import { userTypeColors } from '../utils/colorHandler';
import ColorChip from './common/ColorChip';
import { ManagementTableHeaderCell } from './common/ManagementTable';
import { EmptyValue } from './common/EmptyValue';

// Shape returned by GET /user/family-services (one entry per family member).
interface FamilyServiceRegistration {
  id: number;
  serviceType: string;
  subtype: string;
  serviceInvoiceName: string;
  visitType: string;
  visitBalance: number;
  joined: boolean;
  sos: string;
  eos: string;
}

interface FamilyServiceTrial {
  id: number;
  subtype: string;
  trialDate: string;
  result: string;
}

interface FamilyServiceMember {
  userId: number;
  firstName: string;
  lastName: string;
  userType: string;
  registrations: FamilyServiceRegistration[];
  trialBookings: FamilyServiceTrial[];
}

interface FamilyTreeDialogProps {
  open: boolean;
  onClose: () => void;
  /** All accounts in the group whose eye icon was clicked. */
  users: User[];
  /** The value the group shares (e.g. the phone number), shown as a subtitle. */
  sharedLabel?: React.ReactNode;
}

const LINE = '#cbd5e1';

const displayName = (user: User): string =>
  user.preferredName?.trim() ||
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
  `#${user.id}`;

const NodeBox: React.FC<{ user: User }> = ({ user }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 200,
      minHeight: 84,
      border: `1px solid ${LINE}`,
      borderRadius: '12px',
      padding: '16px 24px',
      backgroundColor: 'background.paper',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      whiteSpace: 'nowrap',
    }}
  >
    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
      {displayName(user)}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        #{user.id}
      </Typography>
      <ColorChip label={user.userType} palette={userTypeColors} />
    </Stack>
  </Box>
);

// A row of boxes joined left-to-right by short horizontal line segments.
const ConnectedRow: React.FC<{ users: User[] }> = ({ users }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'nowrap',
    }}
  >
    {users.map((user, index) => (
      <React.Fragment key={user.id}>
        {index > 0 && <Box sx={{ width: 40, height: '2px', bgcolor: LINE }} />}
        <NodeBox user={user} />
      </React.Fragment>
    ))}
  </Box>
);

const formatDate = (value?: string): string =>
  value ? new Date(value).toLocaleDateString() : '';

// "Services" tab — all registrations + trial bookings for the family, grouped
// by member. Fetches lazily when the tab is first shown.
const FamilyServicesTab: React.FC<{ userIds: number[] }> = ({ userIds }) => {
  const [members, setMembers] = useState<FamilyServiceMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userIds.length === 0) return;
    let active = true;
    setLoading(true);
    apiGet<{ data: FamilyServiceMember[] }>(
      `/user/family-services?ids=${userIds.join(',')}`,
    )
      .then((res) => {
        if (active) setMembers(res.data);
      })
      .catch(() => {
        if (active) setError('Unable to load family services.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userIds]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  const totalServices = members.reduce(
    (sum, member) =>
      sum + member.registrations.length + member.trialBookings.length,
    0,
  );

  if (totalServices === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No services found for this family.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {members
        .filter(
          (member) =>
            member.registrations.length > 0 || member.trialBookings.length > 0,
        )
        .map((member) => (
          <Box key={member.userId}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() ||
                  `#${member.userId}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{member.userId}
              </Typography>
              <ColorChip label={member.userType} palette={userTypeColors} />
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <ManagementTableHeaderCell>Type</ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>
                    Service
                  </ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>
                    Sub-type
                  </ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>
                    Package / Result
                  </ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>
                    Sessions left
                  </ManagementTableHeaderCell>
                  <ManagementTableHeaderCell>Date</ManagementTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {member.registrations.map((reg) => (
                  <TableRow key={`reg-${reg.id}`}>
                    <TableCell>
                      <ColorChip label="Registration" />
                    </TableCell>
                    <TableCell>{reg.serviceType || <EmptyValue />}</TableCell>
                    <TableCell>{reg.subtype || <EmptyValue />}</TableCell>
                    <TableCell>
                      {reg.serviceInvoiceName || <EmptyValue />}
                    </TableCell>
                    <TableCell>{reg.visitBalance}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(reg.sos)}
                    </TableCell>
                  </TableRow>
                ))}
                {member.trialBookings.map((trial) => (
                  <TableRow key={`trial-${trial.id}`}>
                    <TableCell>
                      <ColorChip label="Trial" />
                    </TableCell>
                    <TableCell>
                      <EmptyValue label="—" />
                    </TableCell>
                    <TableCell>{trial.subtype || <EmptyValue />}</TableCell>
                    <TableCell>{trial.result}</TableCell>
                    <TableCell>
                      <EmptyValue label="—" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(trial.trialDate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
    </Stack>
  );
};

const FamilyTreeDialog: React.FC<FamilyTreeDialogProps> = ({
  open,
  onClose,
  users,
  sharedLabel,
}) => {
  const [tab, setTab] = useState<'tree' | 'services'>('tree');
  // Stable id list so the services tab doesn't refetch on every re-render.
  const userIds = React.useMemo(() => users.map((user) => user.id), [users]);

  // Reset to the tree tab whenever the dialog is reopened.
  useEffect(() => {
    if (open) setTab('tree');
  }, [open]);
  // Row 1 = adults, row 2 = everyone else (children). If there are no adults,
  // show everyone on the top row.
  const adults = users.filter(
    (user) => (user.userType || '').toUpperCase() === 'ADULT',
  );
  const others = users.filter(
    (user) => (user.userType || '').toUpperCase() !== 'ADULT',
  );
  const topRow = adults.length > 0 ? adults : users;
  const bottomRow = adults.length > 0 ? others : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.25}>
          <Typography variant="h6" fontWeight={700}>
            Family tree
          </Typography>
          {sharedLabel && (
            <Typography variant="body2" color="text.secondary">
              {sharedLabel}
            </Typography>
          )}
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close family tree"
        >
          <Close />
        </IconButton>
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          sx={{ mt: 1, minHeight: 0 }}
        >
          <Tab value="tree" label="Tree" sx={{ minHeight: 0 }} />
          <Tab value="services" label="Services" sx={{ minHeight: 0 }} />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers>
        {users.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No accounts to display.
          </Typography>
        ) : tab === 'tree' ? (
          <Box sx={{ overflowX: 'auto', py: 4, px: 2, minHeight: 320 }}>
            <Stack
              alignItems="center"
              spacing={0}
              sx={{ minWidth: 'fit-content' }}
            >
              <ConnectedRow users={topRow} />
              {bottomRow.length > 0 && (
                <>
                  {/* Vertical link between the two rows. */}
                  <Box sx={{ width: '2px', height: 48, bgcolor: LINE }} />
                  <ConnectedRow users={bottomRow} />
                </>
              )}
            </Stack>
          </Box>
        ) : (
          <FamilyServicesTab userIds={userIds} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FamilyTreeDialog;
